import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import moment from 'moment';
import 'react-dates/initialize';
import 'react-dates/lib/css/_datepicker.css';
import { SingleDatePicker } from 'react-dates';
import Textarea from 'react-textarea-autosize';
import {
  FormGroup,
  FormFeedback,
  FormText,
  Label,
  Input,
} from 'reactstrap';
import { injectIntl } from 'react-intl';

import { showModal } from '../../../actions';

import AdminUserEditAvatar from './Avatar';
import AdminEditButtons from '../EditButtons';
import AdminUserEditUserFields from './UserFields';
import AdminUserEditUserSettings from './UserSettings';
import UpgradePlanButton from '../../UpgradePlanButton';

import {
  userIsAdmin,
  userIsSuperAdmin,
} from '../../../helpers/Auth';
import { getFormattedDateTime } from '../../../helpers/DateTime';
import { userHasFeature } from '../../../helpers/Features';

import ErrorBoundary from '../../ErrorBoundary';

class AdminUserEditProfileEdit extends PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      datePickerFocused: false,
    };

    this.handleDeactivate = this.handleDeactivate.bind(this);
    this.handleActivate = this.handleActivate.bind(this);
  }

  handleDeactivate(e) {
    e.preventDefault();
    const {
      profile,
      loadUserProfile,
      openUserDeactivateModal,
    } = this.props;

    openUserDeactivateModal({
      className: 'modal-deactivate-user',
      users: [profile],
      backdrop: true,
      afterDeactivateUsers: () => {
        loadUserProfile();
      },
    });
  }

  handleActivate(e) {
    e.preventDefault();
    const {
      profile,
      loadUserProfile,
      openUserActivateModal,
    } = this.props;

    openUserActivateModal({
      className: 'modal-activate-user',
      users: [profile],
      backdrop: true,
      afterActivateUsers: () => {
        loadUserProfile();
      },
    });
  }

  render() {
    const {
      intl,
      profile,
      settings,
      lastUpdate,
      isSelfEdit,
      errors,
      unlocking,
      handleChange,
      handleChangeAvatar,
      handleSendWelcomeEmails,
      handleChangeNextPasswordChangeDate,
      handleCancelEmailChange,
      handleUnlock,
    } = this.props;
    const { datePickerFocused } = this.state;

    return (
      <ErrorBoundary>
        <div className="tab-content-wrapper">
          <h4>
            {
              isSelfEdit ?
                intl.formatMessage({ defaultMessage: 'Your profile' }) :
                intl.formatMessage({ defaultMessage: 'User profile' })
            }
          </h4>
          {isSelfEdit ? (
            <p>
              {intl.formatMessage({ defaultMessage: 'Edit your profile details.' })}
            </p>
          ) : (
            <p>
              {intl.formatMessage({ defaultMessage: 'Edit user profile details.' })}
            </p>
          )}
          <div className="profile-form">
            <AdminUserEditAvatar
              {...this.props}
              handleChangeAvatar={handleChangeAvatar}
            />
            <div className="profile-details">
              {errors.error && (
                <p className="text-danger">
                  {errors.error}
                </p>
              )}
              {profile.deactivated_at && (
                <p className="text-danger">
                  <i className="fas fa-ban" />{' '}
                  {intl.formatMessage({ defaultMessage: 'This account is inactive' })}
                </p>
              )}
              {(!isSelfEdit && profile.uuid) && (
                <>
                  {profile.is_locked_out && (
                    <p>
                      <strong>
                        {intl.formatMessage({ defaultMessage: 'This account has been locked out because of too many failed login attempts' })}
                      </strong>
                      {userIsSuperAdmin() && (
                        <>
                          {' '}
                          {unlocking ? (
                            `...`
                          ) : (
                            <a href="#unlock" onClick={handleUnlock}>
                              {intl.formatMessage({ defaultMessage: 'Unlock' })}
                            </a>
                          )}
                        </>
                      )}
                    </p>
                  )}
                  {profile.activated_by && (
                    <p>
                      {intl.formatMessage({ defaultMessage: 'This account has been approved by' })}{' '}
                      {profile.activated_by.name}.
                    </p>
                  )}
                  <p>
                    {profile.last_login_at ? (
                      <span>
                        <strong>
                          {intl.formatMessage({ defaultMessage: 'Last login:' })}
                        </strong>{' '}
                        {getFormattedDateTime(profile.last_login_at)}
                      </span>
                    ) : (
                      <span>
                        {profile.activation_email_sent_at ? (
                          <>
                            <strong>
                              {intl.formatMessage({ defaultMessage: 'This account has not been activated.' })}
                            </strong><br />
                            {intl.formatMessage({ defaultMessage: 'A welcome email was sent on' })}{' '}
                            {getFormattedDateTime(profile.activation_email_sent_at)}.{' '}
                            <Link
                              to="#send"
                              onClick={(e) => {
                                e.preventDefault();
                                handleSendWelcomeEmails([profile.uuid]);
                              }}
                            >
                              {intl.formatMessage({ defaultMessage: 'Re-send email' })}
                            </Link>
                          </>
                        ) : (
                          <>
                            <strong>
                              {intl.formatMessage({ defaultMessage: 'This account has not been activated.' })}
                            </strong><br />
                            {intl.formatMessage({ defaultMessage: 'Send the welcome email to activate.' })}{' '}
                            <Link
                              to="#send"
                              onClick={(e) => {
                                e.preventDefault();
                                handleSendWelcomeEmails([profile.uuid]);
                              }}
                            >
                              {intl.formatMessage({ defaultMessage: 'Send email' })}
                            </Link>
                          </>
                        )}
                      </span>
                    )}
                  </p>
                </>
              )}
              {profile.next_password_change_at && (
                <div className="next-password-change">
                  <strong>{intl.formatMessage({ defaultMessage: 'Next password change:' })}</strong>{' '}
                  {getFormattedDateTime(profile.next_password_change_at)}
                  {(userIsAdmin && !isSelfEdit) && (
                    <>
                      <SingleDatePicker
                        id="inpUserNextPasswordChange"
                        date={moment(profile.next_password_change_at)}
                        focused={datePickerFocused}
                        onFocusChange={({ focused }) => {
                          this.setState({ datePickerFocused: focused });
                        }}
                        onDateChange={handleChangeNextPasswordChangeDate}
                        numberOfMonths={1}
                        withPortal={true}
                        hideKeyboardShortcutsPanel={true}
                        noBorder={true}
                        isOutsideRange={() => false}
                      />
                      {' '}
                      <a
                        href="#change-date"
                        title={intl.formatMessage({ defaultMessage: 'Modify next password change date' })}
                        onClick={(e) => {
                          e.preventDefault();
                          this.setState({ datePickerFocused: true });
                        }}
                      >
                        <i className="far fa-calendar" />
                      </a>
                    </>
                  )}
                </div>
              )}
              {profile.terms_accepted_at && (
                <p>
                  <strong>{intl.formatMessage({ defaultMessage: 'Terms accepted at:' })}</strong>{' '}
                  {getFormattedDateTime(profile.terms_accepted_at)}
                  {profile.terms_accepted_ip && (
                    <>
                      {' '}({profile.terms_accepted_ip})
                    </>
                  )}
                </p>
              )}
              <FormGroup>
                <Label for="inpUserName">
                  {intl.formatMessage({ defaultMessage: 'First name' })}
                </Label>
                <Input
                  type="text"
                  name="first_name"
                  id="inpUserFirstName"
                  placeholder={intl.formatMessage({ defaultMessage: 'First name' })}
                  defaultValue={profile.first_name}
                  onChange={handleChange}
                  invalid={Boolean(errors.first_name)}
                />
                {errors.first_name && (
                  <FormFeedback>{errors.first_name}</FormFeedback>
                )}
              </FormGroup>
              <FormGroup>
                <Label for="inpUserName">
                  {intl.formatMessage({ defaultMessage: 'Last name' })}
                </Label>
                <Input
                  type="text"
                  name="last_name"
                  id="inpUserLastName"
                  placeholder={intl.formatMessage({ defaultMessage: 'Last name' })}
                  defaultValue={profile.last_name}
                  onChange={handleChange}
                  invalid={Boolean(errors.last_name)}
                />
                {errors.last_name && (
                  <FormFeedback>{errors.last_name}</FormFeedback>
                )}
              </FormGroup>
              <FormGroup>
                <Label for="inpUserEmail">
                  {intl.formatMessage({ defaultMessage: 'Email' })}
                </Label>
                <Input
                  type="email"
                  name="email"
                  id="inpUserEmail"
                  placeholder={intl.formatMessage({ defaultMessage: 'Email address' })}
                  value={profile.email}
                  onChange={handleChange}
                  invalid={Boolean(errors.email)}
                />
                {isSelfEdit && (
                  <FormText>
                    {intl.formatMessage({
                      defaultMessage: 'If you change your email, it will not become active until you confirm it.',
                    })}
                  </FormText>
                )}
                {(isSelfEdit && profile.pending_email) && (
                  <FormText color="danger">
                    {intl.formatMessage({
                      defaultMessage: 'There is a pending change of your email to {pendingEmail}.',
                    }, { pendingEmail: profile.pending_email })}
                    {' '}
                    <a href="#cancel" onClick={handleCancelEmailChange}>
                      {intl.formatMessage({ defaultMessage: 'Cancel' })}
                    </a>
                  </FormText>
                )}
                {errors.email && (
                  <FormFeedback>{errors.email}</FormFeedback>
                )}
              </FormGroup>
              {!profile.uuid && (
                <FormGroup check>
                  <Label for="inpSendWelcomeEmail">
                    <Input
                      type="checkbox"
                      id="inpSendWelcomeEmail"
                      name="send_welcome_email"
                      className="input-check check-s form-check-input no-margin"
                      defaultChecked={true}
                      onChange={handleChange}
                    />
                    {' '}
                    {intl.formatMessage({
                      defaultMessage: 'Send the new user a welcome email to activate their account',
                    })}
                  </Label>
                </FormGroup>
              )}
              {profile.send_welcome_email && (
                <FormGroup>
                  <Label for="inpNote" className="user-note">
                    {intl.formatMessage({ defaultMessage: 'Add an optional note' })}
                    {!userHasFeature('user-invite-note') && (
                      <UpgradePlanButton
                        feature="user-invite-note"
                        simple={true}
                        forceUpgrade={true}
                      />
                    )}
                  </Label>
                  <Textarea
                    className="form-control"
                    disabled={!userHasFeature('user-invite-note')}
                    name="note"
                    id="inpNote"
                    onInput={handleChange}
                  />
                </FormGroup>
              )}
              {!profile.is_superadmin && (
                <AdminUserEditUserFields
                  profile={profile}
                  errors={errors}
                  handleChange={handleChange}
                />
              )}
              <AdminUserEditUserSettings
                profile={profile}
                settings={settings}
                lastUpdate={lastUpdate}
                isSelfEdit={isSelfEdit}
                handleChange={handleChange}
              />
            </div>
          </div>
        </div>
        <AdminEditButtons
          {...this.props}
          deleteText={
            profile.deactivated_at ?
              intl.formatMessage({ defaultMessage: 'Re-activate user' }) :
              intl.formatMessage({ defaultMessage: 'Deactivate user' })
          }
          hideDelete={(isSelfEdit || !profile.uuid)}
          handleDelete={profile.deactivated_at ? this.handleActivate : this.handleDeactivate}
        />
      </ErrorBoundary>
    );
  }
}

const mapDispatchToProps = dispatch => ({
  openUserDeactivateModal: (params) => {
    dispatch(showModal('UserDeactivateModal', params));
  },
  openUserActivateModal: (params) => {
    dispatch(showModal('UserActivateModal', params));
  },
});

AdminUserEditProfileEdit.propTypes = {
  intl: PropTypes.object.isRequired,
  profile: PropTypes.object,
  settings: PropTypes.object,
  lastUpdate: PropTypes.instanceOf(Date),
  isSelfEdit: PropTypes.bool.isRequired,
  unlocking: PropTypes.bool.isRequired,
  errors: PropTypes.object.isRequired,
  handleChange: PropTypes.func.isRequired,
  handleChangeAvatar: PropTypes.func.isRequired,
  loadUserProfile: PropTypes.func.isRequired,
  openUserDeactivateModal: PropTypes.func.isRequired,
  openUserActivateModal: PropTypes.func.isRequired,
  handleSendWelcomeEmails: PropTypes.func.isRequired,
  handleChangeNextPasswordChangeDate: PropTypes.func.isRequired,
  handleCancelEmailChange: PropTypes.func.isRequired,
  handleUnlock: PropTypes.func.isRequired,
};

AdminUserEditProfileEdit.defaultProps = {
  profile: null,
  settings: {},
  lastUpdate: null,
};

export default connect(null, mapDispatchToProps)(injectIntl(AdminUserEditProfileEdit));
